import { Trans, useTranslation } from 'react-i18next'
import { WebhookEventType } from '@/api/dashboard'
import { Checkbox, CheckboxLabel, CollapseView, ExternalLink, InputSearch, cn } from '@dashboard/ui'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { webhooksQuery } from '@/layouts/webhooks/api'
import { useGenerateCompanyPath } from '@/libs'

interface SelectEventsStageModalProps {
  value: WebhookEventType[] | null | undefined
  onChange: (value: WebhookEventType[]) => void
  className?: string
  currentWebhookId?: string
}

export const GROUPED_EVENTS: Record<string, WebhookEventType[]> = {
  Main: [WebhookEventType.PlayerVerify, WebhookEventType.ItemAdd, WebhookEventType.ItemRemove],
  Order: [WebhookEventType.OrderPaid, WebhookEventType.OrderCanceled, WebhookEventType.OrderRefunded],
  Payment: [
    WebhookEventType.PaymentSucceeded,
    WebhookEventType.PaymentCanceled,
    WebhookEventType.PaymentChargeback,
    WebhookEventType.PaymentDeclined,
    WebhookEventType.PaymentDispute,
    WebhookEventType.PaymentExpired,
    WebhookEventType.PaymentPending,
    WebhookEventType.PaymentRefunded,
    WebhookEventType.PaymentRejected,
    WebhookEventType.PaymentVoided,
  ],
  Player: [WebhookEventType.PlayerLookup, WebhookEventType.PlayerIsIdle],
  Other: [
    WebhookEventType.StoreGet,
    WebhookEventType.IngamePush,
    WebhookEventType.IngamePopup,
    WebhookEventType.CouponRedeemed,
  ],
}

export const DOC_LINKS: Record<string, string> = {
  [WebhookEventType.PlayerVerify]: 'https://docs.aghanim.com/webhooks/verify-player',
  [WebhookEventType.ItemAdd]: 'https://docs.aghanim.com/webhooks/item-add',
  [WebhookEventType.ItemRemove]: 'https://docs.aghanim.com/webhooks/item-remove',
  [WebhookEventType.OrderCanceled]: 'https://docs.aghanim.com/webhooks/order-canceled',
  [WebhookEventType.OrderPaid]: 'https://docs.aghanim.com/webhooks/order-paid',
  [WebhookEventType.OrderRefunded]: 'https://docs.aghanim.com/webhooks/refund',
  [WebhookEventType.CouponRedeemed]: 'https://docs.aghanim.com/webhooks/redeem-code',
  [WebhookEventType.StoreGet]: 'https://docs.aghanim.com/webhooks/store-get',
  [WebhookEventType.IngamePopup]: 'https://docs.aghanim.com/webhooks/in-game-popup',
  [WebhookEventType.IngamePush]: 'https://docs.aghanim.com/webhooks/mobile-push',
  [WebhookEventType.PlayerLookup]:
    'https://docs.aghanim.com/advanced/composite-authorization#step-1-handle-playerlookup-webhook-for-partial-identity',
  Payment: 'https://docs.aghanim.com/webhooks/payment-events',
}

export const ALL_EVENTS: WebhookEventType[] = Object.values(GROUPED_EVENTS).flat()

export const SelectEventsList = (props: SelectEventsStageModalProps) => {
  const { t } = useTranslation()
  const { companyId, gameId } = useGenerateCompanyPath()
  const { data: webhooks = [] } = useQuery(webhooksQuery(companyId, gameId))
  const [search, setSearch] = useState<string>()

  const addedEvents = webhooks
    .filter(wh => wh.id !== props.currentWebhookId)
    .map(wh => wh.events)
    .flat()

  const allAvailableEvents = ALL_EVENTS.filter(it => !addedEvents.includes(it))

  return (
    <div className={props.className}>
      <InputSearch
        autoFocus
        value={search}
        onFocus={e => {
          e.stopPropagation()
        }}
        onMouseDown={e => {
          e.stopPropagation()
        }}
        onChange={e => {
          setSearch(e.target.value)
        }}
        placeholder={t('SearchInputPlaceHolder')}
      />

      <Checkbox
        className="mt-3"
        disabled={!!search}
        checked={props.value?.length === allAvailableEvents.length}
        onChange={e => {
          if (e.target.checked) {
            props.onChange([...allAvailableEvents])
          } else {
            props.onChange([])
          }
        }}
      >
        {t('webhook.form.select-all')}
      </Checkbox>

      {Object.entries(GROUPED_EVENTS).map(([group, events], index) => {
        if (search) {
          events = events.filter(event => event.toLowerCase().includes(search.toLowerCase()))
        }

        if (events.length === 0) {
          return null
        }

        const count = events.length
        const selectedCount = (props.value || []).filter(event => events.includes(event)).length
        const extraRight = (
          <div className="flex gap-1">
            {selectedCount > 0 && <span className="text-text-brand-primary">{selectedCount}</span>}
            <span className="text-text-quarterary-hover">
              {selectedCount > 0 && '/'}
              <Trans i18nKey={'webhook.events'} values={{ count: count }} />
            </span>
          </div>
        )

        const docGroupLink = DOC_LINKS[group]

        return (
          <CollapseView label={group} extraRight={extraRight} key={group} initialOpened={index == 0}>
            {events.map((event, idx) => {
              const docLink = DOC_LINKS[event] || docGroupLink
              const checked = props.value?.includes(event)
              return (
                <div className={cn('px-3 pt-3', checked && 'bg-fg-primary-alt')} key={`event-${event}`}>
                  <CheckboxLabel
                    checkbox={
                      <Checkbox
                        disabled={!checked && addedEvents.includes(event)}
                        id={`event-${event}`}
                        checked={checked}
                        onChange={e => {
                          const newValue = e.target.checked
                            ? [...(props.value || []), event]
                            : (props.value || []).filter(v => v !== event)
                          props.onChange(newValue)
                        }}
                      />
                    }
                    htmlFor={`event-${event}`}
                    caption={
                      <div
                        className={cn(
                          events.length > idx + 1 ? ' border-b ' : '',
                          'pb-3',
                          checked ? 'border-border-secondary-hover' : 'border-border-secondary',
                        )}
                      >
                        {t(`webhook.event.${event}.desc`)}
                        {docLink && (
                          <a
                            href={docLink}
                            target="_blank"
                            className="mt-1 flex items-center gap-1 text-text-brand-primary"
                          >
                            {t('webhook.discover.learn-more')}
                            <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                    }
                  >
                    <code>{t(`webhook.event.${event}.label`)}</code>
                  </CheckboxLabel>
                </div>
              )
            })}
          </CollapseView>
        )
      })}
    </div>
  )
}
